import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '@services/auth.service';

/** Shape of a NestJS HttpException's JSON body. */
interface ApiErrorBody {
  statusCode?: number;
  error?: string;
  message?: string;
}

// Structural, not `instanceof HttpErrorResponse`: the retry path's error can be
// a plain `{ error }`-shaped object (e.g. AuthService.refresh() test doubles),
// not just a real HttpErrorResponse from the HTTP client.
const getApiErrorBody = (error: unknown): ApiErrorBody | undefined =>
  (error as { error?: ApiErrorBody } | null | undefined)?.error;

export const queryInterceptor: HttpInterceptorFn = (req, next) => {
  const notifService = inject(NzNotificationService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const clonedReq = req.clone({ withCredentials: true });

  const isLoginRoute = clonedReq.url.endsWith('/auth/login');
  const isRefreshRoute = clonedReq.url.endsWith('/auth/refresh');

  const reportGenericError = (errObj: ApiErrorBody | undefined) => {
    if (errObj?.error) {
      notifService.error(errObj.error, errObj.message ?? '');
    } else {
      notifService.error('Unknown Error', 'Please contact support');
    }
  };

  // Status-code-aware reporting for an ordinary business request. `isLogin`
  // suppresses only the 401 case's "session expired" + navigate — there's no
  // session yet to expire, and nowhere else to send the user — while still
  // giving login its own 400/404/429/500 messages, same as everywhere else.
  const reportError = (errObj: ApiErrorBody | undefined, isLogin = false) => {
    switch (errObj?.statusCode) {
      case 400:
        notifService.error(errObj.error ?? 'Bad Request', errObj.message ?? '');
        break;
      case 401:
        if (isLogin) {
          reportGenericError(errObj); // e.g. "Wrong password"
        } else {
          notifService.error('Your session expired', 'Please login again');
          authService.onSessionConfirmedDead();
          void router.navigate(['/login']);
        }
        break;
      case 404:
        notifService.error(
          'Resource Not Found',
          'Looks like the data you try to get does not exist',
        );
        break;
      case 429:
        notifService.error('Too Many Requests', 'Try again later');
        break;
      case 500:
        notifService.error('An error occured', 'Try again latter or contact support');
        break;
      default:
        reportGenericError(errObj);
        break;
    }
  };

  if (isLoginRoute) {
    return next(clonedReq).pipe(
      catchError((error: unknown) => {
        reportError(getApiErrorBody(error), true);
        return throwError(() => error);
      }),
    );
  }

  if (isRefreshRoute) {
    // Never reported here. This request only ever runs nested inside
    // another request's own catchError below (or the AuthService leader
    // timer, which reports nothing on purpose) — reporting it again here
    // would show the same "session expired" toast twice for one failure.
    return next(clonedReq);
  }

  return next(clonedReq).pipe(
    catchError((error: unknown) => {
      const errObj = getApiErrorBody(error);
      if (errObj?.statusCode === 401) {
        return authService.refresh().pipe(
          switchMap(() => next(clonedReq)),
          // Whatever comes back from a failed refresh, or from retrying
          // this request after a successful one, is judged on its own
          // merits — only a genuine 401/403 means the session is actually
          // dead. A 429/5xx/network hiccup fails just this one request; it
          // does not sign the user out.
          catchError((retryError: unknown) => {
            reportError(getApiErrorBody(retryError));
            return throwError(() => retryError);
          }),
        );
      }
      reportError(errObj);
      return throwError(() => error);
    }),
  );
};
